import React, { useMemo, useState } from 'react';
import { useAdsState } from '../hooks/useAdsState';
import { AdsConnectionFailed } from '../components/AdsConnectionFailed';
import { useAdsActivity, useAdsRecap } from '../hooks/useActivity';
import type { ActivityRecord, RecapRange } from '../api/campaignsApi';
import { PageHeader } from '@/shared/ui';
import { formatConversions } from '@/shared/utils/format';
import styles from './AdsActivityPage.module.css';

const RANGE_OPTIONS: Array<{ value: RecapRange; label: string }> = [
  { value: 'last7', label: 'Last 7 days' },
  { value: 'last30', label: 'Last 30 days' },
  { value: 'last90', label: 'Last 90 days' },
  { value: 'ytd', label: 'Year to date' },
  { value: 'lifetime', label: 'Lifetime' },
];

// ─── Category → bucket / label / rail colour ────────────────────────────────
// Real ads categories: launch | creative | optimization | strategy | monitor |
// sync | audit | connection | setup. Routine work (syncs, audits, connection,
// setup) is collapsed away from the owner read; everything else is meaningful.
type Bucket = 'launch' | 'optim' | 'alert' | 'routine';

const CATEGORY_BUCKET: Record<string, Bucket> = {
  launch: 'launch',
  creative: 'launch',
  optimization: 'optim',
  strategy: 'optim',
  monitor: 'alert',
  sync: 'routine',
  audit: 'routine',
  connection: 'routine',
  setup: 'routine',
};

const CATEGORY_LABEL: Record<string, string> = {
  launch: 'Launch',
  creative: 'Creative',
  optimization: 'Optimization',
  strategy: 'Strategy',
  monitor: 'Alert',
  sync: 'Sync',
  audit: 'Audit',
  connection: 'Setup',
  setup: 'Setup',
};

const BUCKET_RAIL: Record<Bucket, string> = {
  launch: 'var(--accent)',
  optim: 'var(--success)',
  alert: 'var(--warning)',
  routine: 'var(--gray-400)',
};

const FILTERS: Array<{ value: 'all' | Bucket; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'launch', label: 'Launches' },
  { value: 'optim', label: 'Optimizations' },
  { value: 'alert', label: 'Alerts' },
  { value: 'routine', label: 'Routine' },
];

function bucketOf(category: string): Bucket {
  return CATEGORY_BUCKET[category] ?? 'optim';
}
function labelOf(category: string): string {
  return CATEGORY_LABEL[category] ?? category;
}
function railOf(category: string): string {
  return BUCKET_RAIL[bucketOf(category)];
}

function formatDay(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}
function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

function groupByDate(records: ActivityRecord[]): Array<{ date: string; items: ActivityRecord[] }> {
  const groups = new Map<string, ActivityRecord[]>();
  for (const r of records) {
    const key = formatDay(r.createdAt);
    const arr = groups.get(key) ?? [];
    arr.push(r);
    groups.set(key, arr);
  }
  return Array.from(groups.entries()).map(([date, items]) => ({ date, items }));
}

/**
 * Ads Activity — two reads of the same feed. "Owner view" surfaces a weekly
 * recap plus the changes that affect results, with routine sync/audit work
 * collapsed into a single line. "All activity" is the full, filterable log.
 */
export function AdsActivityPage() {
  const adsState = useAdsState();
  const [view, setView] = useState<'owner' | 'detail'>('owner');
  const [range, setRange] = useState<RecapRange>('last7');
  const [filter, setFilter] = useState<'all' | Bucket>('all');
  const [query, setQuery] = useState('');

  const recapQuery = useAdsRecap(range);
  const activityQuery = useAdsActivity();

  const records = useMemo(() => activityQuery.data?.data ?? [], [activityQuery.data]);

  const highlights = useMemo(
    () => records.filter((r) => bucketOf(r.category) !== 'routine').slice(0, 8),
    [records],
  );
  const routineCounts = useMemo(() => {
    let sync = 0;
    let audit = 0;
    for (const r of records) {
      if (r.category === 'sync') sync++;
      else if (r.category === 'audit') audit++;
    }
    return { sync, audit };
  }, [records]);

  const detailRecords = useMemo(() => {
    const q = query.toLowerCase().trim();
    return records.filter((r) => {
      const bucketOk = filter === 'all' || bucketOf(r.category) === filter;
      const qOk = !q || r.message.toLowerCase().includes(q) || r.category.toLowerCase().includes(q);
      return bucketOk && qOk;
    });
  }, [records, filter, query]);

  const grouped = useMemo(() => groupByDate(detailRecords), [detailRecords]);

  if (!adsState.loaded) {
    return (
      <div className={styles.page}>
        <PageHeader title="Activity" />
      </div>
    );
  }

  if (adsState.accounts.connectionFailed) {
    return (
      <AdsConnectionFailed
        title="Activity"
        className={styles.page}
        failures={adsState.accounts.failed}
      />
    );
  }

  if (!adsState.accounts.any) {
    return (
      <div className={styles.page}>
        <PageHeader
          title="Activity"
          subtitle="Activity will appear here once your campaigns are running."
        />
      </div>
    );
  }

  const recap = recapQuery.data?.data;
  const showRecap = adsState.strategy === 'active' || adsState.campaigns !== 'none';

  function formatDelta(value: number | null) {
    if (value == null) return null;
    const sign = value > 0 ? '+' : '';
    return `${sign}${value.toFixed(0)}%`;
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title="Activity"
        subtitle="A weekly read of what changed, and the full log when you want every detail."
      />

      {/* View switch */}
      <div className={styles.tabs} role="tablist" aria-label="Activity views">
        <button
          type="button"
          role="tab"
          aria-selected={view === 'owner'}
          className={`${styles.tab} ${view === 'owner' ? styles.tabActive : ''}`}
          onClick={() => setView('owner')}
        >
          Owner view
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={view === 'detail'}
          className={`${styles.tab} ${view === 'detail' ? styles.tabActive : ''}`}
          onClick={() => setView('detail')}
        >
          All activity{' '}
          {records.length > 0 && <span className={styles.tabCount}>{records.length}</span>}
        </button>
      </div>

      {/* ─── OWNER VIEW ─────────────────────────────────────────────────────── */}
      {view === 'owner' && (
        <>
          {showRecap && (
            <div className={styles.recapCard}>
              <div className={styles.recapHeader}>
                <span className={styles.recapTitle}>Recap</span>
                <select
                  className={styles.rangeSelect}
                  value={range}
                  onChange={(e) => setRange(e.target.value as RecapRange)}
                  aria-label="Recap range"
                >
                  {RANGE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.recapMetrics}>
                <div className={styles.recapMetric}>
                  <div className={styles.recapMetricLabel}>Ad Spend</div>
                  <div className={styles.recapMetricValue}>${recap?.spend.toFixed(0) ?? '—'}</div>
                  <div className={styles.recapMetricDelta}>
                    {formatDelta(recap?.spendDeltaPct ?? null) ?? '—'}
                  </div>
                </div>
                <div className={styles.recapMetric}>
                  <div className={styles.recapMetricLabel}>Conversions</div>
                  <div className={styles.recapMetricValue}>
                    {recap?.conversions != null ? formatConversions(recap.conversions) : '—'}
                  </div>
                  <div className={styles.recapMetricDelta}>
                    {formatDelta(recap?.conversionsDeltaPct ?? null) ?? '—'}
                  </div>
                </div>
                <div className={styles.recapMetric}>
                  <div className={styles.recapMetricLabel}>CPA</div>
                  <div className={styles.recapMetricValue}>${recap?.cpa.toFixed(2) ?? '—'}</div>
                  <div className={styles.recapMetricDelta}>
                    {formatDelta(recap?.cpaDeltaPct ?? null) ?? '—'}
                  </div>
                </div>
              </div>
              {recap?.narrative && <div className={styles.recapNarrative}>{recap.narrative}</div>}
            </div>
          )}

          <div className={styles.sectionRow}>
            <h2 className={styles.sectionTitle}>What changed</h2>
            <span className={styles.sectionHint}>The moves that affect your results</span>
          </div>

          {highlights.length === 0 ? (
            <div className={styles.empty}>
              No notable changes yet — routine work is running in the background.
            </div>
          ) : (
            <div className={styles.list}>
              {highlights.map((r) => (
                <div
                  key={r.id}
                  className={styles.item}
                  style={{ borderLeftColor: railOf(r.category) }}
                >
                  <div>
                    <div className={styles.itemCat} style={{ color: railOf(r.category) }}>
                      {labelOf(r.category)}
                    </div>
                    <div className={styles.itemTitle}>{r.message}</div>
                  </div>
                  <div className={styles.itemWhen}>
                    {formatDay(r.createdAt)} · {formatTime(r.createdAt)}
                  </div>
                </div>
              ))}
            </div>
          )}

          {(routineCounts.sync > 0 || routineCounts.audit > 0) && (
            <div className={styles.rollup}>
              <span>
                {routineCounts.sync > 0 && (
                  <>
                    Synced Google &amp; Meta <b>{routineCounts.sync}</b> time
                    {routineCounts.sync === 1 ? '' : 's'}
                  </>
                )}
                {routineCounts.sync > 0 && routineCounts.audit > 0 && ' and '}
                {routineCounts.audit > 0 && (
                  <>
                    ran <b>{routineCounts.audit}</b> health check
                    {routineCounts.audit === 1 ? '' : 's'}
                  </>
                )}{' '}
                recently — nothing here needed your attention.
              </span>
              <button type="button" className={styles.rollupMore} onClick={() => setView('detail')}>
                See all →
              </button>
            </div>
          )}
        </>
      )}

      {/* ─── DETAIL VIEW ────────────────────────────────────────────────────── */}
      {view === 'detail' && (
        <>
          <div className={styles.toolbar}>
            <input
              type="text"
              className={styles.search}
              placeholder="Search campaigns, actions, terms…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Search activity"
            />
            <div className={styles.filters} role="group" aria-label="Filter by type">
              {FILTERS.map((f) => (
                <button
                  key={f.value}
                  type="button"
                  className={`${styles.filterBtn} ${filter === f.value ? styles.filterBtnActive : ''}`}
                  aria-pressed={filter === f.value}
                  onClick={() => setFilter(f.value)}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {detailRecords.length === 0 ? (
            <div className={styles.empty}>
              {records.length === 0
                ? 'No activity recorded yet.'
                : 'No activity matches those filters.'}
            </div>
          ) : (
            grouped.map((group) => (
              <div key={group.date} className={styles.dayGroup}>
                <div className={styles.dayLabel}>{group.date}</div>
                <div className={styles.log}>
                  {group.items.map((r) => {
                    const routine = bucketOf(r.category) === 'routine';
                    return (
                      <div
                        key={r.id}
                        className={`${styles.logRow} ${routine ? styles.logRowRoutine : ''}`}
                      >
                        <span className={styles.logTime}>{formatTime(r.createdAt)}</span>
                        <span className={styles.logCat} style={{ color: railOf(r.category) }}>
                          {labelOf(r.category)}
                        </span>
                        <span className={styles.logMessage}>{r.message}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </>
      )}
    </div>
  );
}
